import React, { useState, useRef, useEffect } from "react";
import { Input } from "../../../../ui/input";
import { ChevronDownIcon, XIcon } from "lucide-react";
import { toast } from "sonner";

interface SingleSelectOption {
  label: string;
  value: string;
  meta?: string;
  tooltip?: string;
}

interface SingleSelectWithClearProps {
  value?: string | null;
  onChange: (value: string | null) => void;
  placeholder: string;
  options: SingleSelectOption[];
  className?: string;
}

const SingleSelectWithClear: React.FC<SingleSelectWithClearProps> = ({
  value,
  onChange,
  placeholder,
  options,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(opt => opt.value === value);
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Scroll dropdown into view when it opens
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      setTimeout(() => {
        dropdownRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest"
        });
      }, 10); // Small delay to ensure DOM is updated
    }
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setSearchTerm("");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const handleInputClick = () => {
    if (!isOpen) {
      // When opening, prefill search term with currently selected value
      if (selectedOption) {
        setSearchTerm(selectedOption.label);
      }
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      
      // Only check for matches if there's actually a search term
      if (searchTerm.trim()) {
        // Check if the search term exactly matches any option
        const exactMatch = options.find(option => 
          option.label.toLowerCase() === searchTerm.toLowerCase()
        );
        
        if (exactMatch) {
          handleSelect(exactMatch.value);
        } else {
          // Show error message for invalid node identifier
          toast.error(`No node with identifier "${searchTerm}" found`);
        }
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setSearchTerm(""); // Clear search term when closing via Escape
    }
  };

  const displayValue = selectedOption ? selectedOption.label : "";

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <Input
          value={isOpen ? searchTerm : displayValue}
          onChange={handleInputChange}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="pr-16"
          readOnly={!isOpen}
        />
        
        <div className="absolute inset-y-0 right-0 flex items-center pr-2">
          {selectedOption && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-gray-200 rounded mr-1 text-gray-500 hover:text-gray-700"
            >
              <XIcon className="w-4 h-4" />
            </button>
          )}
          <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </div>
      </div>

      {isOpen && filteredOptions.length > 0 && (
        <div 
          ref={dropdownRef}
          className="absolute z-[9999] w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-60 overflow-y-auto"
        >
          {filteredOptions.map((option) => (
            <div
              key={option.value}
              className="px-3 py-2 cursor-pointer text-sm relative group hover:bg-accent hover:text-accent-foreground"
              onClick={() => handleSelect(option.value)}
            >
              <div className="flex items-center justify-between">
                <span className="truncate flex-1 mr-2 block" style={{ maxWidth: "200px" }}>
                  {option.label}
                </span>
                {option.meta && (
                  <span className="text-xs px-2 py-0.5 bg-muted rounded text-muted-foreground flex-shrink-0">
                    {option.meta}
                  </span>
                )}
              </div>
              
              {/* Custom tooltip for full name */}
              {option.tooltip && (
                <div className="absolute left-full top-0 ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[10001]">
                  {option.tooltip}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[9998]"
          onClick={() => {
            setIsOpen(false);
            setSearchTerm(""); // Clear search term when closing
          }}
        />
      )}
    </div>
  );
};

export default SingleSelectWithClear;
export type { SingleSelectOption, SingleSelectWithClearProps };